import { useEffect, useState } from "react";
import { ArrowLeft, Check, Copy, FileMinus2, Mail, Send, XCircle } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import AppShell from "../components/AppShell";
import CancelInvoiceModal from "../components/CancelInvoiceModal";
import CreditNoteModal from "../components/CreditNoteModal";
import { api } from "../lib/api";

const money = new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN", maximumFractionDigits: 2 });

const STATUS_META = {
  paid: { label: "Pagada", color: "#238a65", bg: "#dcf7ed" },
  pending: { label: "Pendiente", color: "#a97a11", bg: "#fff2c9" },
  sent: { label: "Enviada", color: "#4169e1", bg: "#eef4ff" },
  draft: { label: "Borrador", color: "#7c3aed", bg: "#f3e8ff" },
  cancelled: { label: "Cancelada", color: "#d7565d", bg: "#ffe5e6" },
  overdue: { label: "Vencida", color: "#283237", bg: "#e6e8ea" },
};

export default function InvoiceDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [inv, setInv] = useState(null);
  const [creditNotes, setCreditNotes] = useState([]);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [cancelOpen, setCancelOpen] = useState(false);
  const [creditNoteOpen, setCreditNoteOpen] = useState(false);

  const refresh = () => {
    api.get(`/invoices/sent/${id}`, { withCredentials: true })
      .then((r) => setInv(r.data))
      .catch((e) => setError(e.response?.data?.detail || "No pudimos cargar la factura."));
    api.get(`/invoices/sent/${id}/credit-notes`, { withCredentials: true })
      .then((r) => setCreditNotes(r.data || []))
      .catch(() => setCreditNotes([]));
  };
  useEffect(() => { refresh(); }, [id]);

  const markStatus = async (status) => {
    try {
      await api.put(`/invoices/sent/${id}`, { status }, { withCredentials: true });
      setNotice(`Estado actualizado a "${STATUS_META[status]?.label}".`);
      refresh();
    } catch (issue) { setError(issue.response?.data?.detail || "No pudimos actualizar."); }
  };

  const duplicate = async () => {
    try {
      const r = await api.post(`/invoices/sent/${id}/duplicate`, {}, { withCredentials: true });
      navigate(`/facturas/${r.data.id}`);
    } catch (issue) { setError(issue.response?.data?.detail || "No pudimos duplicar."); }
  };

  if (!inv) return <AppShell><main className="page"><p>{error || "Cargando..."}</p></main></AppShell>;

  const meta = STATUS_META[inv.computed_status] || STATUS_META.sent;
  const item = inv.item || {};

  return (
    <AppShell>
      <main className="page invoice-detail-page">
        <button className="detail-back" onClick={() => navigate("/facturas")} data-testid="detail-back"><ArrowLeft size={16} />Facturas Enviadas</button>

        <header className="detail-head">
          <div>
            <small className="eyebrow">FACTURA</small>
            <h1>{inv.folio}</h1>
            <p><span className="sent-status" style={{ background: meta.bg, color: meta.color }}>{meta.label}</span> · Emitida {(inv.created_at || "").slice(0, 10)}</p>
          </div>
          <div className="detail-head-actions">
            {inv.email_url && <a href={inv.email_url} className="expenses-scan-button" data-testid="detail-send-email"><Mail size={15} />Enviar por correo</a>}
            <button className="expenses-scan-button" onClick={duplicate} data-testid="detail-duplicate"><Copy size={15} />Duplicar</button>
            {inv.computed_status !== "paid" && inv.computed_status !== "cancelled" && <button className="expenses-add-button" onClick={() => markStatus("paid")} data-testid="detail-mark-paid"><Check size={15} />Marcar pagada</button>}
            {inv.computed_status !== "cancelled" && inv.computed_status !== "draft" && (
              <button className="expenses-scan-button" onClick={() => setCreditNoteOpen(true)} data-testid="detail-credit-note"><FileMinus2 size={15} />Nota de crédito</button>
            )}
            {inv.computed_status !== "cancelled" && inv.computed_status !== "draft" && (
              <button className="expenses-scan-button" style={{ color: "#d7565d", borderColor: "#f2b6b9" }} onClick={() => setCancelOpen(true)} data-testid="detail-cancel-cfdi"><XCircle size={15} />Cancelar CFDI</button>
            )}
          </div>
        </header>

        {notice && <p className="expenses-notice">{notice}</p>}
        {error && <p className="expenses-error">{error}</p>}

        {inv.computed_status === "cancelled" && (
          <article className="invoice-cancelled-banner" data-testid="detail-cancellation-banner">
            <XCircle size={20} />
            <div>
              <b>CFDI cancelado</b>
              <small>
                Motivo <b>{inv.cancellation_motive || "—"}</b> · {inv.cancellation_motive_label || "Sin motivo registrado"}
                {inv.cancellation_reason ? <> · {inv.cancellation_reason}</> : null}
                {inv.cancellation_substitution_uuid ? <><br />Sustituye por UUID <code>{inv.cancellation_substitution_uuid}</code></> : null}
                <br />
                <em>Estado ante SAT: {inv.cancellation_sat_status === "cancelada_sat" ? "confirmado" : "pendiente de confirmación"}</em>
              </small>
            </div>
          </article>
        )}

        <section className="detail-grid">
          <article data-testid="detail-client">
            <b>Cliente</b>
            <dl>
              <div><dt>Nombre</dt><dd>{inv.receiver_name}</dd></div>
              <div><dt>RFC</dt><dd>{inv.receiver_rfc}</dd></div>
              <div><dt>Correo</dt><dd>{inv.receiver_email || "—"}</dd></div>
              <div><dt>Uso CFDI</dt><dd>{inv.usage_code || "G03"}</dd></div>
              <div><dt>CP receptor</dt><dd>{inv.receiver_zip || "—"}</dd></div>
              <div><dt>Régimen fiscal</dt><dd>{inv.receiver_tax_system || "—"}</dd></div>
            </dl>
          </article>

          <article data-testid="detail-payment">
            <b>Pago</b>
            <dl>
              <div><dt>Método</dt><dd>{inv.payment_method || "—"}</dd></div>
              <div><dt>Forma</dt><dd>{inv.payment_form || "—"}</dd></div>
              <div><dt>Moneda</dt><dd>MXN</dd></div>
              <div><dt>Versión CFDI</dt><dd>4.0</dd></div>
              <div><dt>UUID</dt><dd>{inv.facturapi_invoice_id || "Pendiente de timbrar"}</dd></div>
              <div><dt>Vencimiento</dt><dd>{inv.due_date || "—"}</dd></div>
            </dl>
          </article>

          <article className="detail-items" data-testid="detail-items">
            <b>Conceptos</b>
            <table>
              <thead><tr><th>Descripción</th><th>Cantidad</th><th>Precio unitario</th><th>Importe</th></tr></thead>
              <tbody>
                <tr>
                  <td>{item.description || "—"}</td>
                  <td>{item.quantity || 1}</td>
                  <td>{money.format(Number(item.unit_price || 0))}</td>
                  <td>{money.format(Number(item.quantity || 1) * Number(item.unit_price || 0))}</td>
                </tr>
              </tbody>
            </table>
            <div className="detail-totals">
              <div><span>Subtotal</span><b>{money.format(Number(inv.subtotal || 0))}</b></div>
              <div><span>IVA (16%)</span><b>{money.format(Number(inv.iva || 0))}</b></div>
              <div className="grand"><span>Total</span><b>{money.format(Number(inv.total || 0))}</b></div>
            </div>
          </article>

          <article data-testid="detail-timeline">
            <b>Actividad</b>
            <ul className="detail-timeline">
              <li><i style={{ background: "#4169e1" }} /><div><b>Factura creada</b><small>{(inv.created_at || "").slice(0, 16).replace("T", " ")}</small></div></li>
              {inv.facturapi_invoice_id && <li><i style={{ background: "#28a878" }} /><div><b>CFDI timbrado</b><small>UUID {inv.facturapi_invoice_id}</small></div></li>}
              {inv.email_status === "sent" && <li><i style={{ background: "#f7c34e" }} /><div><b>Correo enviado</b><small>Al cliente</small></div></li>}
              {inv.paid_at && <li><i style={{ background: "#238a65" }} /><div><b>Pago registrado</b><small>{(inv.paid_at || "").slice(0, 16).replace("T", " ")}</small></div></li>}
              {inv.computed_status === "cancelled" && <li><i style={{ background: "#d7565d" }} /><div><b>Cancelada</b><small>Marcada por el usuario</small></div></li>}
              {(inv.activity || []).map((entry) => (
                <li key={entry.id || entry.created_at}><i style={{ background: "#79858c" }} /><div><b>{entry.title}</b><small>{entry.detail || (entry.created_at || "").slice(0, 16).replace("T", " ")}</small></div></li>
              ))}
            </ul>
          </article>
        </section>

        {creditNotes.length > 0 && (
          <section className="detail-credit-notes" data-testid="detail-credit-notes">
            <h2>Notas de crédito emitidas ({creditNotes.length})</h2>
            <table className="expenses-table">
              <thead>
                <tr>
                  <th>Folio</th>
                  <th>Fecha</th>
                  <th>Concepto</th>
                  <th>Total</th>
                  <th>Estado SAT</th>
                </tr>
              </thead>
              <tbody>
                {creditNotes.map((nc) => (
                  <tr key={nc.id} data-testid={`credit-note-row-${nc.id}`}>
                    <td><b>{nc.folio}</b></td>
                    <td>{(nc.created_at || "").slice(0, 10)}</td>
                    <td>
                      <b>{nc.item?.description || "—"}</b>
                      {nc.reason && <small>{nc.reason}</small>}
                    </td>
                    <td><strong style={{ color: "#d7565d" }}>−{money.format(Number(nc.total || 0))}</strong></td>
                    <td>
                      <span className="sent-status" style={{ background: "#fff2c9", color: "#a97a11" }}>
                        {nc.sat_status === "cancelada_sat" ? "Confirmada SAT" : "Pendiente SAT"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}

        {cancelOpen && (
          <CancelInvoiceModal
            invoice={inv}
            onClose={() => setCancelOpen(false)}
            onCancelled={() => {
              setCancelOpen(false);
              setNotice("CFDI cancelado. Estado pendiente de confirmación SAT.");
              refresh();
            }}
          />
        )}
        {creditNoteOpen && (
          <CreditNoteModal
            invoice={inv}
            onClose={() => setCreditNoteOpen(false)}
            onCreated={(nc) => {
              setCreditNoteOpen(false);
              setNotice(`Nota de crédito ${nc.folio} emitida.`);
              refresh();
            }}
          />
        )}
      </main>
    </AppShell>
  );
}
